'use client'

// The subtle notice that v2 adjusted a prompt to meet the content rules (lib/prompt-adjusted.ts, ai_app_backend
// #1508): it fades in near the top, fades out by itself after a few seconds, and a tap closes it sooner. Mounted once
// in the root layout; it renders nothing until an order's placing answer says `adjusted: true`. Fixed, so it never
// moves the page; with reduced motion it appears and goes without the fade.

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ADJUSTED_EVENT,
  type AdjustedNotice,
  adjustedNoticeKeys,
} from '../lib/prompt-adjusted.ts'

/** How long the notice stays, and how long its fade takes. */
const SHOWN_MS = 4500
const FADE_MS = 300

export default function PromptAdjustedToast() {
  const { t } = useTranslation()
  const [notice, setNotice] = useState<AdjustedNotice | null>(null)
  const [visible, setVisible] = useState(false)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])

  const clear = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }

  const hide = () => {
    clear()
    setVisible(false)
    timers.current.push(setTimeout(() => setNotice(null), FADE_MS))
  }

  useEffect(() => {
    const show = (event: Event) => {
      const detail = (event as CustomEvent<AdjustedNotice>).detail
      if (!detail) return
      clear()
      setNotice(detail)
      // One frame hidden first, so the fade in runs.
      timers.current.push(setTimeout(() => setVisible(true), 20))
      timers.current.push(
        setTimeout(() => {
          setVisible(false)
          timers.current.push(setTimeout(() => setNotice(null), FADE_MS))
        }, SHOWN_MS),
      )
    }
    window.addEventListener(ADJUSTED_EVENT, show)
    return () => {
      window.removeEventListener(ADJUSTED_EVENT, show)
      clear()
    }
  }, [])

  return (
    // A polite live region, without role="status": it is mounted on every page, and a second status there would
    // clash with each page's own (the chat's notice, for one).
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 z-[115] flex justify-center px-4"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
    >
      {notice && (
        <button
          type="button"
          onClick={hide}
          data-track="create.adjusted_notice_close"
          className={`pointer-events-auto max-w-sm rounded-xl bg-black/80 px-4 py-2.5 text-left text-sm leading-snug text-white/90 shadow-lg ring-1 ring-white/10 backdrop-blur transition duration-300 motion-reduce:transition-none ${
            visible ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
          }`}
        >
          {adjustedNoticeKeys(notice)
            .map((key) => t(key))
            .join(' ')}
        </button>
      )}
    </div>
  )
}
